import React, { useState, useEffect } from 'react';
import { useHttp } from '../hooks/useHttp';
import { useActiveAccount } from 'thirdweb/react';
import { logger } from '../utils/logger';
import { LogViewer } from '../components/LogViewer';

interface ChipData {
  uid: string;
  key: string;
  toyType: number;
  ctr: number;
  nfcSid: string | null;
  cookieTimestamp: string | null;
  walletAddress: string | null;
  claimed: boolean;
  tokenId: string | null;
}

export function NfcSimulator() {
  const account = useActiveAccount();
  const address = account?.address;
  const [chipNumber, setChipNumber] = useState<number>(1);
  const [chipData, setChipData] = useState<ChipData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scanUrl, setScanUrl] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const { isLoading: httpLoading, error: httpError, sendRequest } = useHttp();

  // Only show in development
  if (process.env.REACT_APP_NODE_ENV === 'production') {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="mx-auto max-w-md text-center">
          <h1 className="mb-4 text-2xl font-bold text-gray-900">
            Access Denied
          </h1>
          <p className="text-gray-600">
            This page is only available in development mode.
          </p>
        </div>
      </div>
    );
  }

  const handleDisplayInfo = async () => {
    logger.nfc(`Displaying info for chip number`, { chipNumber });
    setIsLoading(true);
    setError(null);
    
    try {
      const { data } = await sendRequest<ChipData>({
        endpoint: `/api/dev/nfc-info?chipNumber=${chipNumber}`,
        method: 'GET',
      });
      logger.nfc(`Chip info retrieved successfully`, { 
        chipNumber, 
        uid: (data as any).uid, 
        toyType: (data as any).toyType, 
        ctr: (data as any).ctr,
        claimed: (data as any).claimed,
        tokenId: (data as any).tokenId
      });
      
      setChipData(data as any);
    } catch (err) {
      logger.nfcError('Failed to load chip info', err as Error, { chipNumber });
      setError(err instanceof Error ? err.message : 'Failed to load chip info');
    } finally {
      setIsLoading(false);
    }
  };

  const handleScanChip = async () => {
    logger.nfc(`Scanning chip number`, { chipNumber });
    setIsScanning(true);
    setError(null);
    
    try {
      const { data } = await sendRequest<{ url: string }>({
        endpoint: `/api/dev/nfc-scan?chipNumber=${chipNumber}`,
        method: 'POST',
      });
      logger.nfc(`Chip scan successful, URL generated`, { 
        chipNumber, 
        url: (data as any).url,
        urlLength: (data as any).url.length 
      });
      
      setScanUrl((data as any).url);
      
      // Refresh chip data
      logger.nfc(`Refreshing chip data after scan`, { chipNumber });
      await handleDisplayInfo();
    } catch (err) {
      logger.nfcError('Failed to scan chip', err as Error, { chipNumber });
      setError(err instanceof Error ? err.message : 'Failed to scan chip');
    } finally {
      setIsScanning(false);
    }
  };

  const openScanUrl = () => {
    if (scanUrl) {
      logger.ui(`Opening scan URL in new tab`, { url: scanUrl });
      window.open(scanUrl, '_blank');
    }
  };

  return (
    <div className="py-8 min-h-screen bg-gray-50">
      <div className="px-4 mx-auto max-w-4xl">
        <div className="mb-8 text-center">
          <img
            src="/images/223-logo.png"
            alt="223 Logo"
            className="mx-auto mb-4 w-[100px]"
          />
          <h1 className="mb-2 text-3xl font-bold text-gray-900">
            NFC Chip Simulator
          </h1>
          <p className="text-gray-600">
            Development tool for testing NFC chip functionality
          </p>
          <div className="px-4 py-3 mt-4 text-yellow-700 bg-yellow-100 rounded border border-yellow-400">
            <p className="text-sm">
              ⚠️ This tool is only available in development mode
            </p>
          </div>
        </div>

        <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
          <h2 className="mb-4 text-xl font-semibold">Chip Controls</h2>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Chip Number (1-{/* This would be dynamic based on CSV data */}
                100)
              </label>
              <input
                type="number"
                min="1"
                max="100"
                value={chipNumber}
                onChange={(e) => {
                  const newChipNumber = parseInt(e.target.value) || 1;
                  logger.ui(`Chip number changed`, {
                    oldValue: chipNumber,
                    newValue: newChipNumber,
                  });
                  setChipNumber(newChipNumber);
                }}
                className="px-3 py-2 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col justify-end space-y-2">
              <button
                onClick={handleDisplayInfo}
                disabled={isLoading}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? "Loading..." : "Display Info"}
              </button>

              <button
                onClick={handleScanChip}
                disabled={isScanning}
                className="px-4 py-2 text-white bg-green-600 rounded-lg transition-colors hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isScanning ? "Scanning..." : "Scan Chip"}
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="px-4 py-3 mb-6 text-red-700 bg-red-100 rounded border border-red-400">
            {error}
          </div>
        )}

        {scanUrl && (
          <div className="p-6 mb-6 bg-white rounded-lg shadow-md">
            <h3 className="mb-4 text-lg font-semibold">Generated Scan URL</h3>
            <div className="p-4 mb-4 bg-gray-100 rounded-lg">
              <p className="font-mono text-sm break-all">{scanUrl}</p>
            </div>
            <button
              onClick={openScanUrl}
              className="px-4 py-2 text-white bg-black rounded-lg transition-colors hover:bg-gray-800"
            >
              Open Scan URL
            </button>
          </div>
        )}

        {chipData && (
          <div className="p-6 bg-white rounded-lg shadow-md">
            <h3 className="mb-4 text-lg font-semibold">Chip Information</h3>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">UID:</span>
                  <p className="p-2 font-mono text-sm bg-gray-100 rounded">
                    {chipData.uid}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">Key:</span>
                  <p className="p-2 font-mono text-sm bg-gray-100 rounded">
                    {chipData.key}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">Toy Type:</span>
                  <p className="text-sm">{chipData.toyType}</p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">Scan Count:</span>
                  <p className="text-sm">
                    This chip was scanned {chipData.ctr} times
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">NFC SID:</span>
                  <p className="p-2 font-mono text-sm bg-gray-100 rounded">
                    {chipData.nfcSid || "Not set"}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">
                    Cookie Timestamp:
                  </span>
                  <p className="text-sm">
                    {chipData.cookieTimestamp
                      ? new Date(chipData.cookieTimestamp).toLocaleString()
                      : "Not set"}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">
                    Last Wallet:
                  </span>
                  <p className="font-mono text-sm">
                    {chipData.walletAddress || "Not bound"}
                  </p>
                </div>

                <div>
                  <span className="font-medium text-gray-700">
                    Digital Collectible Status:
                  </span>
                  <p className="text-sm">
                    {chipData.claimed ? (
                      <span className="text-green-600">
                        Claimed (Token ID: {chipData.tokenId})
                      </span>
                    ) : (
                      <span className="text-orange-600">Not claimed</span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Log Viewer for Development */}
      <div className="mt-8">
        <LogViewer />
      </div>
    </div>
  );
}
